// A provider answering "Not Found", and who that is actually about.
//
// The upstream sends back two words. The card printed them, and the user read
// them as a verdict on whatever model they thought they were using: one report
// said «при продолжении прерванной сессии с DeepSeek V4 Flash пишет "Not
// Found"», while the fleet events for that install show DeepSeek answering
// normally and the 404s coming from providers they had added themselves —
// `qwen/qwen`, and a VIDEO GENERATION model (`veo-3.1-lite-generate-preview`)
// called through a chat API. Both are the same mistake: a model id or a base
// URL that the endpoint does not serve.
//
// So the card names the provider and the model, and says which of the two
// kinds of "not found" this is. The distinction matters because the remedies
// have nothing in common: for a provider the user configured, the fix is in
// their own settings; for one we broker, they cannot fix anything and telling
// them to check a base URL would send them looking for a field that does not
// exist.

// Our own proxy, as it appears in the failing request's URL. Whether a provider
// is BROKERED cannot be decided from its id: `deepseek` is our route for most
// installs and a direct BYOK connection for others (an `auth` key or a
// provider block in opencode.json both keep the id), and the same is true of
// GigaChat. Deciding by id would tell a user whose OWN DeepSeek key has a wrong
// model id that "this is nothing you configured" — sending them to write to us
// instead of to their settings, which is the exact confusion this whole change
// is about.
export const GATEWAY_HOST = /(^|\.)((gigatool\.app)|(multitool\.works))$/i

// GigaChat is "ours" whichever way it is routed, and the URL alone cannot say
// so: the SDK is built with Sber's own base and the rewrite to our proxy
// happens INSIDE its fetch, so a brokered failure still reports
// gigachat.sberdevices.ru. Treating that as the user's own endpoint would send
// them to correct a model id and a server address that this provider does not
// let them set — the same inverted advice, moved to a different provider. It
// reads the same for a BYOK GigaChat account: the credentials are theirs, the
// endpoint and the model list are not.
const FIXED_UPSTREAM_HOST = /(^|\.)sberdevices\.ru$/i

export type ModelNotFound = {
  provider: string
  model: string
  /** True when the refusal came from OUR proxy; the user has no setting to correct. */
  brokered: boolean
}

/** Is this an endpoint the user neither chose nor can change? */
function notTheirEndpoint(url: unknown): boolean {
  if (typeof url !== "string") return false
  try {
    const host = new URL(url).hostname
    return GATEWAY_HOST.test(host) || FIXED_UPSTREAM_HOST.test(host)
  } catch {
    return false
  }
}

/**
 * Is this failure the upstream saying "no such model/endpoint here"?
 *
 * Keyed on the HTTP status first, because that is unambiguous. The text is a
 * fallback for error shapes that carry no status, and is deliberately narrow —
 * "not found" appears inside plenty of unrelated messages (a missing file in a
 * tool result, for one), so only a message that is *just* those two words
 * qualifies.
 */
export function modelNotFound(input: {
  status?: unknown
  text?: string
  provider?: string
  model?: string
  /** The URL the failing request was sent to (APIError metadata). */
  url?: unknown
}): ModelNotFound | undefined {
  // A status, when there is one, is the answer — the text fallback exists only
  // for error shapes that carry none (the stream-error path builds one). A 500
  // whose body happens to read "Not Found" is not the upstream saying it has no
  // such model, and saying so would send the user to check an id that is fine.
  if (typeof input.status === "number") {
    if (input.status !== 404) return undefined
  } else if (!(typeof input.text === "string" && /^\s*not found\.?\s*$/i.test(input.text))) {
    return undefined
  }
  const provider = (input.provider ?? "").trim()
  const model = (input.model ?? "").trim()
  // Without a name there is nothing to add to the upstream's own two words.
  if (!provider || !model) return undefined
  return { provider, model, brokered: notTheirEndpoint(input.url) }
}
